<template>
  <!-- 音频录音与实时转写组件根容器 -->
  <div class="audio-recorder">
    <!-- 主交互按钮：切换开始/停止录音状态
      - @click：触发录音状态切换事件
      - :class：动态添加状态类（recording：录音中；connecting：连接中）
      - :disabled：连接过程中禁用按钮，防止重复操作
    -->
    <button
        @click="toggleRecording"
        :class="{ recording: isRecording, connecting: isConnecting }"
        :disabled="isConnecting"
    >
      <!-- 图标：根据录音状态切换（录音中显示停止图标；未录音显示麦克风图标） -->
      <i class="fas" :class="isRecording ? 'fa-stop' : 'fa-microphone'"></i>
      <!-- 按钮文本：与图标状态同步 -->
      <span>{{ isRecording ? '停止录音' : '开始录音' }}</span>
    </button>

    <!-- 状态提示区域：仅在有状态信息时显示（如连接状态、错误信息） -->
    <div class="status" v-if="statusMessage">
      {{ statusMessage }}
    </div>

    <!-- 转写结果展示区域：仅在有识别结果时显示 -->
    <div class="transcription" v-if="transcription">
      <h3>识别结果:</h3>
      <p>{{ transcription }}</p>
    </div>

    <!-- 音频可视化区域：仅在录音状态下显示（展示音频波形柱状图） -->
    <div class="visualizer" v-if="isRecording">
      <div
          class="visualizer-bar"
          :style="{ height: `${visualizerHeight}%` }"
      ></div>
    </div>
  </div>
</template>

<script>
// 导入依赖库：
// - hexMd5：用于生成MD5哈希值，用于WebSocket认证参数计算
// - CryptoJS：提供加密算法（HmacSHA1），用于WebSocket签名生成
// - Base64：用于将加密结果转为Base64编码，适配接口要求
// 注意：已移除js-audio-recorder库依赖，改用原生Web Audio API实现录音功能
import hexMd5 from 'md5';
import HmacSHA1 from 'crypto-js/hmac-sha1';
import CryptoJS from 'crypto-js';

export default {
  data() {
    return {
      // -------------- 录音相关状态 --------------
      isRecording: false, // 录音状态标记（true：录音中；false：未录音）
      audioContext: null, // 音频上下文实例（Web Audio API核心，管理音频处理流程）
      analyserNode: null, // 音频分析节点（用于获取音频频率数据，实现可视化）
      workletNode: null,  // 音频工作节点（自定义音频处理，用于获取PCM音频数据）
      visualizerHeight: 0, // 音频可视化柱状图高度（0-100%）
      visualizerInterval: null, // 可视化定时器（控制柱状图更新频率）
      mediaStream: null,  // 媒体流实例（从麦克风获取的音频流）
      sourceNode: null,   // 媒体流源节点（将mediaStream转为音频上下文可处理的数据源）

      // -------------- WebSocket相关状态 --------------
      websocket: null, // WebSocket实例（用于与实时转写接口建立连接）
      isConnected: false, // WebSocket连接状态（true：已连接；false：未连接）
      isConnecting: false, // WebSocket连接中状态（防止连接过程中重复触发）
      wsServerUrl: this.getWebSocketUrl(), // WebSocket连接地址（调用方法生成带认证的URL）
      APPID: 115865947, // 应用ID（接口认证参数，此处为备用）
      APPKEY: "CRFnFD4FnjPFY4xTbC9yhgbQ", // 应用密钥（接口认证参数，此处为备用）
      DEV_PID: 15372, // 识别模型ID（接口参数，此处为备用）

      // -------------- 状态与结果展示 --------------
      statusMessage: '', // 状态提示文本（如"连接中"、"录音中"、错误信息）
      transcription: '' // 语音转写结果（后端返回的文字内容）
    };
  },

  created() {
    // 组件创建时初始化音频相关资源：
    // 1. 先初始化全局音频上下文（提前创建并暂停，避免自动播放限制）
    // 2. 音频上下文就绪后，注册自定义音频工作处理器（Worklet）
    // 3. 捕获初始化错误，更新状态提示并打印日志
    this.initAudioContext().then(() => {
      this.registerAudioWorklet().catch(error => {
        console.error("音频上下文初始化失败:", error);
        this.statusMessage = `无法初始化音频: ${error.message}`;
      });
    });
  },

  beforeUnmount() {
    // 组件卸载前清理资源，防止内存泄漏：
    // 1. 关闭未关闭的音频上下文
    // 2. 调用统一清理方法，处理录音、WebSocket等资源
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
    this.cleanup();
  },

  methods: {
    /**
     * 生成WebSocket连接地址（带身份认证参数）
     * 逻辑：根据接口要求，拼接基础URL与认证参数（appid、ts、signa）
     * @returns {string} 带认证的WebSocket完整URL
     */
    getWebSocketUrl() {
      // 基础WebSocket接口地址（实时转写接口）
      const url = "wss://rtasr.xfyun.cn/v1/ws";
      // 接口认证所需参数（实际项目中建议从配置文件读取）
      const appId = "e2e1829b";
      const secretKey = "210828febf657de99f8e563d311d7053";
      const ts = Math.floor(new Date().getTime() / 1000); // 时间戳（秒级，避免过期）

      // 计算认证签名：
      // 1. 生成signa：appId + ts 的 MD5哈希值
      const signa = hexMd5(appId + ts);
      // 2. 生成signature：signa 经 secretKey 进行 HmacSHA1 加密后，再Base64编码
      const signatureSha = HmacSHA1(signa, secretKey);
      let signature = CryptoJS.enc.Base64.stringify(signatureSha);
      // 3. 对signature进行URI编码，避免特殊字符影响URL解析
      signature = encodeURIComponent(signature);

      // 拼接完整URL并返回
      return `${url}?appid=${appId}&ts=${ts}&signa=${signature}`;
    },

    /**
     * 1. 初始化全局音频上下文（核心修复：避免使用临时上下文，适配自动播放政策）
     * 作用：创建音频处理核心实例，设置采样率与延迟模式，提前暂停等待用户交互
     * @returns {Promise<void>} 初始化完成的Promise
     */
    async initAudioContext() {
      // 创建音频上下文：
      // - 兼容浏览器前缀（window.AudioContext 或 window.webkitAudioContext）
      // - 强制采样率16000Hz（匹配实时转写接口要求，避免采样率转换问题）
      // - 延迟模式设为"interactive"（低延迟，适合实时音频处理场景）
      this.audioContext = new (window.AudioContext)({
        sampleRate: 16000,
        latencyHint: "interactive"
      });

      // 暂停音频上下文：
      // 浏览器自动播放政策限制：音频上下文需用户交互后才能启动，此处先暂停，后续用户点击时恢复
      if (this.audioContext.state === 'running') {
        await this.audioContext.suspend();
      }

      // 打印初始化信息，便于调试
      console.log("音频上下文初始化完成（已暂停），采样率:", this.audioContext.sampleRate);
    },

    /**
     * 2. 注册AudioWorklet处理器（自定义音频处理模块）
     * 作用：加载并注册录音专用的Worklet，用于后续获取PCM格式音频数据
     * @returns {Promise<void>} 注册完成的Promise
     */
    async registerAudioWorklet() {
      // 前置检查：音频上下文未初始化时，抛出错误终止流程
      if (!this.audioContext) {
        throw new Error("音频上下文未初始化，无法注册Worklet");
      }

      // Worklet处理器脚本地址（自定义的音频处理逻辑文件）
      const workletUrl = "/worklet/recording-worklet-processor.js";

      try {
        // 核心操作：使用全局音频上下文注册Worklet模块
        await this.audioContext.audioWorklet.addModule(workletUrl);
        console.log("AudioWorklet处理器注册成功");
        // 标记Worklet已注册，用于后续开始录音时的前置检查
        this.isWorkletRegistered = true;
      } catch (error) {
        // 捕获注册错误，包装错误信息后抛出
        throw new Error(`Worklet注册失败: ${error.message}`);
      } finally {
        // 清理资源：释放Blob URL（若使用Blob创建的Worklet脚本，避免内存泄漏）
        URL.revokeObjectURL(workletUrl);
      }
    },

    /**
     * 切换录音状态（入口方法）
     * 逻辑：根据当前录音状态，调用开始或停止录音方法
     * @returns {Promise<void>} 状态切换完成的Promise
     */
    async toggleRecording() {
      if (this.isRecording) {
        await this.stopRecording();
      } else {
        await this.startRecording();
      }
    },

    /**
     * 3. 开始录音（核心流程）
     * 作用：启动录音前的准备工作，包括WebSocket连接、音频处理初始化、状态更新
     * @returns {Promise<void>} 录音启动完成的Promise
     */
    async startRecording() {
      // 初始化状态：清空历史转写结果，标记连接中，更新状态提示
      this.transcription = '';
      this.isConnecting = true;
      this.statusMessage = '正在连接服务器...';

      try {
        // 前置检查：Worklet未注册时，打印日志并终止流程
        if (!this.isWorkletRegistered) {
          console.log("Worklet未注册，请先注册Worklet");
          return;
        }

        // 步骤1：建立与实时转写接口的WebSocket连接
        await this.connectWebSocket();

        // 步骤2：初始化音频处理链路（麦克风流、节点连接、数据监听）
        await this.initAudioProcessing();

        // 步骤3：更新状态：标记录音中，更新提示文本
        this.isRecording = true;
        this.statusMessage = '正在录音...';
      } catch (error) {
        // 捕获流程中的所有错误，更新状态提示，打印错误日志，清理资源
        this.statusMessage = `出错了: ${error.message}`;
        console.error('录音启动失败:', error);
        await this.cleanup();
      }
    },

    /**
     * 4. 初始化音频处理链路（核心音频逻辑）
     * 作用：构建从麦克风到WebSocket的音频数据流链路，实现音频采集、处理、发送
     * @returns {Promise<void>} 音频处理初始化完成的Promise
     */
    async initAudioProcessing() {
      try {
        // 步骤1：请求麦克风权限，获取音频媒体流
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,                // 单声道（匹配接口要求，减少数据量）
            echoCancellation: true,        // 关闭回声消除（避免音频失真，与后端处理对齐）
            noiseSuppression: true,        // 关闭噪声抑制（保留原始音频，后端统一处理）
            autoGainControl: true,         // 关闭自动增益（避免音量波动，保证音频稳定性）
          }
        });

        // 步骤2：恢复音频上下文（用户已触发点击事件，符合自动播放政策）
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
          console.log("音频上下文已恢复运行");
        }

        // 步骤3：创建媒体流源节点（将麦克风流转为音频上下文可处理的数据源）
        this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
        this.sourceNode.channelCountMode = 'explicit'; // 强制通道模式：显式控制
        this.sourceNode.channelCount = 1; // 强制单声道输出

        // 步骤4：创建音频分析节点（用于音频可视化，获取频率数据）
        this.analyserNode = this.audioContext.createAnalyser();
        this.analyserNode.fftSize = 32; // FFT大小（决定频率数据的精度，32为性能与精度平衡值）
        this.analyserNode.smoothingTimeConstant = 0.8; // 平滑系数（使可视化波形更平稳）

        // 步骤5：创建Worklet节点（核心：使用已注册的处理器处理音频数据）
        this.workletNode = new AudioWorkletNode(
            this.audioContext,
            'recording-worklet-processor' // 处理器名称：必须与Worklet脚本中注册的名称一致
        );

        // 步骤6：监听Worklet发送的PCM数据（将处理后的音频数据发送到WebSocket）
        this.workletNode.port.onmessage = (event) => {
          // 仅当数据类型为PCM且WebSocket已连接时，发送数据
          if (event.data.type === 'PCM_DATA' && this.websocket?.readyState === WebSocket.OPEN) {
            this.websocket.send(event.data.data);
          }
        };

        // 步骤7：向Worklet发送采样率配置（确保Worklet处理的采样率与音频上下文一致）
        this.workletNode.port.postMessage({
          type: 'SET_SAMPLE_RATE',
          sampleRate: this.audioContext.sampleRate
        });

        // 步骤8：连接音频节点链路（仅保留“源→分析器→Worklet”，不连接输出设备）
        this.sourceNode.connect(this.analyserNode);
        this.analyserNode.connect(this.workletNode);
        // 移除 workletNode 到 destination 的连接，避免音频输出到音响
        // this.workletNode.connect(this.audioContext.destination);

        console.log("音频节点链路连接成功");

        // 步骤9：启动音频可视化（实时展示录音波形）
        this.startVisualizer();
      } catch (error) {
        // 捕获音频初始化错误，打印日志并包装错误信息抛出
        console.error('音频处理初始化失败:', error);
        throw new Error(`音频初始化错误: ${error.message}`);
      }
    },

    /**
     * 5. 停止录音（资源清理与状态重置）
     * 作用：终止录音流程，清理音频资源与WebSocket连接，重置状态
     * @returns {Promise<void>} 录音停止完成的Promise
     */
    async stopRecording() {
      // 前置检查：未录音状态下，直接返回
      if (!this.isRecording) return;

      try {
        // 步骤1：向WebSocket发送停止指令（通知后端结束当前转写会话）
        if (this.websocket?.readyState === WebSocket.OPEN) {
          this.websocket.send(JSON.stringify({"end": true}));
        }

        // 步骤2：通知Worklet停止处理，并断开Worklet节点连接
        if (this.workletNode) {
          this.workletNode.port.postMessage({type: 'STOP'});
          this.workletNode.disconnect();
          this.workletNode = null;
        }

        // 步骤3：暂停音频上下文（不关闭，便于下次快速启动）
        if (this.audioContext.state === 'running') {
          await this.audioContext.suspend();
        }

        // 步骤4：清理音频可视化资源
        this.stopVisualizer();

        // 步骤5：停止麦克风流并清理（释放硬件资源）
        if (this.mediaStream) {
          this.mediaStream.getTracks().forEach(track => track.stop());
          this.mediaStream = null;
        }

        // 步骤6：断开媒体流源节点连接并清理
        if (this.sourceNode) {
          this.sourceNode.disconnect();
          this.sourceNode = null;
        }

        // 步骤7：更新状态：标记未录音，更新提示文本，断开WebSocket连接
        this.isRecording = false;
        this.statusMessage = '录音已停止';
        this.disconnectWebSocket();
      } catch (error) {
        // 捕获停止录音错误，打印日志并更新状态提示
        console.error('停止录音失败:', error);
        this.statusMessage = `停止录音错误: ${error.message}`;
      }
    },

    /**
     * 建立与实时转写接口的WebSocket连接
     * 作用：创建WebSocket实例，绑定连接生命周期回调，处理连接状态与数据接收
     * @returns {Promise<void>} 连接建立完成的Promise
     */
    connectWebSocket() {
      return new Promise((resolve, reject) => {
        // 前置检查：已连接状态下，直接resolve
        if (this.isConnected) {
          resolve();
          return;
        }

        // 创建WebSocket实例（使用带认证的URL）
        this.websocket = new WebSocket(this.wsServerUrl);

        // 回调1：连接成功触发
        this.websocket.onopen = () => {
          this.isConnected = true;
          this.isConnecting = false;
          this.statusMessage = '服务器连接成功，准备录音...';
          resolve(); // 通知外层流程连接已成功
        };

        // 回调2：接收服务器消息触发（处理实时转写结果）
        this.websocket.onmessage = (event) => {
          try {
            // 解析服务器返回的JSON数据
            const data = JSON.parse(event.data);

            // 处理"会话开始"响应（仅打印日志，无实际数据）
            if (data.action === 'started') {
              console.log("ASR响应成功：会话已开始");
              return;
            }

            // 解析转写结果数据（按接口返回格式逐层提取文本）
            console.log("响应结果：", data.data);
            const result = JSON.parse(data.data);
            let templateResult = ''; // 临时存储当前批次转写文本

            // 遍历结果结构：rt数组 → ws数组 → cw数组 → 提取文字w
            const rtArr = result.cn.st.rt;
            rtArr.forEach(rt => {
              const wsArr = rt.ws;
              wsArr.forEach(ws => {
                const cwArr = ws['cw'];
                cwArr.forEach(cw => {
                  templateResult += cw.w;
                });
              });
            });

            // 根据结果类型拼接转写文本：
            // - type:1：中间结果（可选是否拼接，此处注释暂不拼接）
            // - type:0：最终结果（拼接至总转写结果）
            if (result.cn.st.type === '1') {
              // this.transcription += templateResult; // 如需显示中间结果，取消注释
            } else if (result.cn.st.type === '0') {
              this.transcription += templateResult;
            } else {
              // 处理未知结果类型，打印日志
              console.log("未知响应类型:", result.cn.st.type);
            }
          } catch (e) {
            // 捕获JSON解析错误（处理非JSON格式的返回数据）
            console.error("解析JSON失败:", e);
            console.error("非JSON格式的返回数据:", event.data);
            // 将原始数据直接作为转写结果展示（用于调试）
            this.transcription = event.data;
          }
        };

        // 回调3：连接错误触发
        this.websocket.onerror = (error) => {
          const errorMsg = error.message || '未知WebSocket错误';
          this.statusMessage = `连接错误: ${errorMsg}`;
          this.isConnecting = false;
          reject(new Error(errorMsg)); // 通知外层流程连接失败
        };

        // 回调4：连接关闭触发
        this.websocket.onclose = (event) => {
          // 更新连接状态
          this.isConnected = false;
          this.isConnecting = false;

          // 根据关闭代码生成提示文本：
          // - 1000：正常关闭；其他代码：异常关闭
          const closeMsg = event.code === 1000
              ? '连接正常关闭'
              : `连接异常关闭 (代码: ${event.code}, 原因: ${event.reason})`;

          // 若处于录音状态，停止录音并更新提示
          if (this.isRecording) {
            this.statusMessage = closeMsg;
            this.stopRecording();
          }
        };
      });
    },

    /**
     * 断开WebSocket连接
     * 作用：主动关闭WebSocket，处理不同连接状态，避免资源泄漏
     */
    disconnectWebSocket() {
      if (this.websocket) {
        // 仅在"连接中"或"已连接"状态下，主动关闭连接（避免重复关闭）
        if (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING) {
          this.websocket.close(1000, '用户主动断开'); // 1000：正常关闭代码，附带关闭原因
        }
        // 清理WebSocket实例与连接状态
        this.websocket = null;
        this.isConnected = false;
      }
    },

    /**
     * 启动音频可视化
     * 作用：通过定时器周期性获取音频频率数据，计算柱状图高度，实现实时波形展示
     */
    startVisualizer() {
      // 前置检查：分析器节点未初始化时，直接返回
      if (!this.analyserNode) return;

      // 获取频率数据缓冲区长度（由analyserNode.fftSize决定，此处为16）
      const bufferLength = this.analyserNode.frequencyBinCount;
      // 创建Uint8数组存储频率数据（每个值范围0-255）
      const dataArray = new Uint8Array(bufferLength);

      // 启动定时器：每50ms更新一次可视化（20fps，兼顾性能与流畅度）
      this.visualizerInterval = setInterval(() => {
        // 步骤1：从分析器节点获取当前频率数据，存入dataArray
        this.analyserNode.getByteFrequencyData(dataArray);

        // 步骤2：计算频率数据的平均值（用于确定柱状图高度）
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;

        // 步骤3：计算柱状图高度（将0-255的平均值映射到0-100%，并限制范围）
        this.visualizerHeight = Math.min(Math.max(0, (average / 255) * 100), 100);
      }, 50);
    },

    /**
     * 停止音频可视化
     * 作用：清理可视化定时器，重置柱状图高度，释放资源
     */
    stopVisualizer() {
      if (this.visualizerInterval) {
        // 清除定时器，避免内存泄漏
        clearInterval(this.visualizerInterval);
        this.visualizerInterval = null;
        // 重置柱状图高度为0
        this.visualizerHeight = 0;
      }
    },

    /**
     * 统一清理所有资源（通用方法）
     * 作用：整合停止录音、断开连接、清空状态等操作，用于组件卸载或异常恢复
     */
    async cleanup() {
      await this.stopRecording(); // 停止录音并清理音频资源
      this.disconnectWebSocket(); // 断开WebSocket连接
      this.transcription = ''; // 清空转写结果
      this.statusMessage = ''; // 清空状态提示
    }
  }
};
</script>

<style scoped lang="less">
// 组件容器样式：控制整体布局与间距
.audio-recorder {
  max-width: 500px; // 限制最大宽度，避免大屏下过于分散
  margin: 2rem auto; // 水平居中，上下留间距
  text-align: center; // 内部元素水平居中
  padding: 1rem; // 内边距，增加呼吸感
}

// 主按钮基础样式：控制按钮外观与交互
button {
  background-color: #4CAF50; // 默认颜色：绿色（未录音状态）
  color: white; // 文字颜色：白色
  border: none; // 取消边框
  border-radius: 50px; // 圆角设计，提升美观度
  padding: 1rem 2rem; // 内边距，保证按钮大小
  font-size: 1.2rem; // 字体大小，提升可读性
  cursor: pointer; // 鼠标悬浮时显示指针，提示可点击
  display: inline-flex; // 弹性布局，使图标与文字对齐
  align-items: center; // 垂直居中对齐
  gap: 0.5rem; // 图标与文字间距
  transition: all 0.3s ease; // 过渡动画，提升交互体验
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); // 阴影效果，增加层次感
}

// 按钮hover效果：未禁用时生效
button:hover:not(:disabled) {
  transform: translateY(-2px); // 轻微上移，模拟按压反馈
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15); // 加深阴影，增强视觉反馈
}

// 录音中按钮样式：红色，区分状态
button.recording {
  background-color: #f44336;
}

// 连接中按钮样式：橙色，区分状态
button.connecting {
  background-color: #ff9800;
}

// 禁用状态按钮样式：降低透明度，取消交互反馈
button:disabled {
  opacity: 0.7; // 透明度降低，提示不可用
  cursor: not-allowed; // 鼠标显示禁止图标
  transform: none; // 取消hover上移动画
  box-shadow: none; // 取消阴影
}

// 状态提示文本样式：控制提示信息外观
.status {
  margin: 1.5rem 0; // 上下间距，与按钮、结果区区分
  color: #666; // 文字颜色：灰色，避免过于醒目
  font-size: 1rem; // 字体大小
  min-height: 1.5rem; // 最小高度，避免布局跳动
  line-height: 1.5; // 行高，提升可读性
}

// 转写结果容器样式：控制结果展示区域外观
.transcription {
  margin-top: 1.5rem; // 上间距，与状态区区分
  padding: 1rem; // 内边距，增加呼吸感
  border-radius: 8px; // 圆角设计
  background-color: #f5f5f5; // 背景色：浅灰色，区分内容区
  text-align: left; // 文字左对齐，提升阅读体验
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); // 轻微阴影，增加层次感
}

// 转写结果标题样式
.transcription h3 {
  margin: 0 0 0.5rem 0; // 下间距，与内容区分
  color: #333; // 文字颜色：深灰色，突出标题
  font-size: 1.1rem; // 字体大小
  font-weight: 600; // 字体加粗，增强标题感
}

// 转写结果文本样式
.transcription p {
  margin: 0; // 取消默认边距
  color: #222; // 文字颜色：近黑色，提升可读性
  line-height: 1.6; // 行高，优化多行文本阅读
  white-space: pre-wrap; // 保留换行符，处理含换行的转写结果
}

// 音频可视化容器样式：控制波形展示区域外观
.visualizer {
  width: 100%; // 宽度铺满父容器
  height: 60px; // 固定高度，保证布局稳定
  display: flex; // 弹性布局，使柱状图居中
  align-items: flex-end; // 柱状图底部对齐
  justify-content: center; // 柱状图水平居中
  margin-top: 1rem; // 上间距，与按钮区分
  padding: 0.5rem 0; // 上下内边距，增加呼吸感
  background-color: #fafafa; // 背景色：极浅灰，区分可视化区域
  border-radius: 4px; // 圆角设计
}

// 音频可视化柱状图样式：控制波形柱子外观
.visualizer-bar {
  width: 8px; // 柱子宽度
  background-color: #2196F3; // 柱子颜色：蓝色，醒目且不刺眼
  border-radius: 4px 4px 0 0; // 仅顶部圆角，模拟波形效果
  transition: height 0.1s ease; // 高度过渡动画，使波形更流畅
  min-height: 2px; // 最小高度，避免完全消失时布局异常
}
</style>